import {
    Alert,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { MessageKey, useI18n } from '../i18n';
import { useAds } from '../monetization/AdsProvider';
import { RootState } from '../store';

import { ChooseDeck } from './components/ChooseDeck';
import { PageShell } from './components/PageShell';
import { ReadingView } from './components/ReadingView';
import { ShuffleDeck } from './components/ShuffleDeck';
import { TarotCard } from './components/TarotCard';
import { cosmos } from './cosmos';
import { dealCards, newReadingId, randomInt, Reading } from './draw';
import { buzz, playCue } from './feedback';
import { SPREADS, SpreadId } from './spreads';
import {
    commitReading,
    dailyFor,
    sealDaily,
    setProfileName,
} from './tarotSlice';

type Step =
    'name' | 'reflect' | 'begin' | 'charging' | 'choose' | 'drawn' | 'reading';

const CHARGE_MS = 3300;
/** How long the person is held in the breathing step before "I am ready" appears. */
const REFLECT_MS = 7000;

const isSpread = (id: string | undefined): id is SpreadId =>
    Boolean(id && id in SPREADS);

/** The breathing circle: swells for four seconds, holds for four, ebbs for four. */
const Breath: React.FC = () => {
    const { t } = useI18n();
    const [phase, setPhase] = useState(0);
    useEffect(() => {
        const id = window.setInterval(() => setPhase((p) => (p + 1) % 3), 4000);
        return () => window.clearInterval(id);
    }, []);
    return (
        <Box sx={{ position: 'relative', width: 200, height: 200, my: 3 }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: '1px solid rgba(212,175,106,.7)',
                    background:
                        'radial-gradient(circle, rgba(157,123,216,.35), rgba(157,123,216,0) 70%)',
                    boxShadow: '0 0 40px rgba(157,123,216,.4)',
                    animation: 'arcana-breath 12s ease-in-out infinite',
                    '@keyframes arcana-breath': {
                        '0%, 100%': { transform: 'scale(0.55)' },
                        '33%, 66%': { transform: 'scale(1)' },
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                    },
                }}
            />
            <Typography
                variant="overline"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    letterSpacing: '0.3em',
                }}
            >
                {t(
                    (
                        [
                            'ritual.breathIn',
                            'ritual.breathHold',
                            'ritual.breathOut',
                        ] as const
                    )[phase]
                )}
            </Typography>
        </Box>
    );
};

/**
 * The ritual that every reading goes through: (name) → breathe → touch the deck → the cosmos charges and
 * bursts → the cards lie face down → a video → the cards turn. The cards are drawn at the burst, before
 * the video, so leaving at that point cannot be used to re-roll them.
 */
export const RitualPage: React.FC = () => {
    const params = useParams();
    if (!isSpread(params.spread)) return <Navigate to="/" replace />;
    return <Ritual spread={params.spread} />;
};

const Ritual: React.FC<{ spread: SpreadId }> = ({ spread: spreadId }) => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { requestUnlock } = useAds();
    const spread = SPREADS[spreadId];
    const tarot = useSelector((s: RootState) => s.tarot);
    const todays = spreadId === 'daily' ? dailyFor(tarot) : null;

    // The card of the day asks once how to address the person; the answer (or skipping) is remembered.
    const isDaily = spreadId === 'daily';
    const asksOwnName = isDaily && !tarot.profile.asked;
    const [name, setName] = useState('');
    const [reading, setReading] = useState<Reading | null>(todays);
    const [step, setStep] = useState<Step>(
        todays ? 'drawn' : spread.needsName || asksOwnName ? 'name' : 'reflect'
    );
    const [canContinue, setCanContinue] = useState(false);
    const [opening, setOpening] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const timers = useRef<number[]>([]);

    useEffect(
        () => () => {
            timers.current.forEach(window.clearTimeout);
            cosmos.setMode('idle');
        },
        []
    );

    useEffect(() => {
        if (step !== 'reflect') return;
        setCanContinue(false);
        const id = window.setTimeout(() => setCanContinue(true), REFLECT_MS);
        return () => window.clearTimeout(id);
    }, [step]);

    // A daily card that was already revealed today goes straight to its reading.
    useEffect(() => {
        if (todays?.unlocked)
            navigate(`/reading/${todays.id}`, { replace: true });
        // Only on arrival: committing the reading below also makes it unlocked.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const begin = useCallback(() => {
        setStep('charging');
        cosmos.setMode('charge');
        playCue('charge');
        // Haptics that quicken with the sound.
        [
            0, 500, 950, 1350, 1700, 2000, 2250, 2450, 2620, 2760, 2880, 2990,
        ].forEach((at) =>
            timers.current.push(window.setTimeout(() => buzz(12 + at / 60), at))
        );
        timers.current.push(
            window.setTimeout(() => {
                cosmos.burst();
                playCue('burst');
                setStep('choose');
            }, CHARGE_MS)
        );
    }, []);

    /** The person has picked their cards: deal them (upright or reversed) and seal the reading. */
    const choose = (ids: string[]) => {
        const drawn: Reading = {
            id: newReadingId(),
            spread: spreadId,
            at: Date.now(),
            name: isDaily
                ? tarot.profile.name || undefined
                : spread.needsName
                  ? name.trim()
                  : undefined,
            me: isDaily ? undefined : tarot.profile.name || undefined,
            cards: dealCards(ids),
            unlocked: false,
            seed: randomInt(1000),
        };
        if (spreadId === 'daily') dispatch(sealDaily(drawn));
        setReading(drawn);
        setStep('drawn');
    };

    const reveal = async () => {
        if (!reading || opening) return;
        setOpening(true);
        setDismissed(false);
        const ok = await requestUnlock();
        setOpening(false);
        if (!ok) {
            setDismissed(true);
            return;
        }
        const unlocked = { ...reading, unlocked: true };
        dispatch(commitReading(unlocked));
        setReading(unlocked);
        playCue('reveal');
        setStep('reading');
    };

    const leave = () => navigate('/');

    if (step === 'reading' && reading)
        return (
            <PageShell onBack={leave} backLabel={t('reading.done')}>
                <ReadingView reading={reading} autoTurn />
            </PageShell>
        );

    return (
        <PageShell onBack={leave} backLabel={t('ritual.abandon')} fill>
            <Stack
                alignItems="center"
                textAlign="center"
                sx={{
                    pt: 1,
                    flex: 1,
                    // On a wide screen the deck sits in the middle of the page rather than under the back button.
                    justifyContent: {
                        xs: 'flex-start',
                        md:
                            step === 'begin' || step === 'charging'
                                ? 'center'
                                : 'flex-start',
                    },
                    pb: { md: step === 'begin' || step === 'charging' ? 6 : 0 },
                }}
            >
                {step === 'name' && (
                    <Stack
                        component="form"
                        spacing={2}
                        alignItems="center"
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (name.trim()) {
                                playCue('ui');
                                if (isDaily)
                                    dispatch(setProfileName(name.trim()));
                                setStep('reflect');
                            }
                        }}
                    >
                        <Typography variant="h5" component="h2">
                            {t(
                                isDaily
                                    ? 'ritual.name.titleDaily'
                                    : 'ritual.name.title'
                            )}
                        </Typography>
                        <Typography color="text.secondary">
                            {t(
                                isDaily
                                    ? 'ritual.name.hintDaily'
                                    : 'ritual.name.hint'
                            )}
                        </Typography>
                        <TextField
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            fullWidth
                            value={name}
                            onChange={(e) =>
                                setName(e.target.value.slice(0, 30))
                            }
                            placeholder={t(
                                isDaily
                                    ? 'ritual.name.placeholderDaily'
                                    : 'ritual.name.placeholder'
                            )}
                            inputProps={{
                                'aria-label': t(
                                    isDaily
                                        ? 'ritual.name.titleDaily'
                                        : 'ritual.name.title'
                                ),
                                maxLength: 30,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '1.4rem',
                                },
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={!name.trim()}
                        >
                            {t('ritual.continue')}
                        </Button>
                        {isDaily && (
                            <Button
                                variant="text"
                                onClick={() => {
                                    dispatch(setProfileName(''));
                                    setStep('reflect');
                                }}
                            >
                                {t('ritual.name.skip')}
                            </Button>
                        )}
                    </Stack>
                )}

                {step === 'reflect' && (
                    <>
                        <Typography variant="h5" component="h2">
                            {spread.needsName
                                ? t('ritual.reflect.titleLove', {
                                      name: name.trim(),
                                  })
                                : t('ritual.reflect.title')}
                        </Typography>
                        <Breath />
                        <Typography
                            color="text.secondary"
                            sx={{ maxWidth: 420 }}
                        >
                            {t(`ritual.reflect.body.${spreadId}` as MessageKey)}
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            disabled={!canContinue}
                            onClick={() => {
                                playCue('swell');
                                setStep('begin');
                            }}
                            sx={{
                                mt: 3,
                                opacity: canContinue ? 1 : 0,
                                transition: 'opacity 1.2s',
                            }}
                        >
                            {t('ritual.ready')}
                        </Button>
                    </>
                )}

                {(step === 'begin' || step === 'charging') && (
                    <>
                        <Typography variant="h5" component="h2">
                            {step === 'charging'
                                ? t('ritual.charging')
                                : t('ritual.begin.title')}
                        </Typography>
                        <ShuffleDeck
                            shuffling={step === 'charging'}
                            onTouch={begin}
                            label={t('ritual.begin.tap')}
                        />
                        {step === 'begin' && (
                            <Typography
                                color="primary"
                                variant="overline"
                                sx={{ letterSpacing: '0.3em' }}
                            >
                                {t('ritual.begin.tap')}
                            </Typography>
                        )}
                    </>
                )}

                {step === 'choose' && (
                    <ChooseDeck
                        positions={spread.positions.map((p) =>
                            t(`pos.${p.key}` as MessageKey, {
                                name: name.trim(),
                            })
                        )}
                        onConfirm={choose}
                    />
                )}

                {step === 'drawn' && reading && (
                    <>
                        <Typography variant="h5" component="h2">
                            {t('ritual.drawn.title')}
                        </Typography>
                        <Stack
                            direction="row"
                            justifyContent="center"
                            flexWrap="wrap"
                            sx={{ gap: 1.5, my: 3 }}
                        >
                            {reading.cards.map((c, i) => (
                                <TarotCard
                                    key={c.id}
                                    cardId={c.id}
                                    name=""
                                    reversed={false}
                                    faceUp={false}
                                    width={
                                        reading.cards.length === 1 ? 150 : 84
                                    }
                                    glow
                                    label={t(
                                        `pos.${spread.positions[i].key}` as MessageKey,
                                        { name: reading.name ?? '' }
                                    )}
                                />
                            ))}
                        </Stack>
                        <Typography
                            color="text.secondary"
                            sx={{ maxWidth: 420, mb: 2 }}
                        >
                            {t('ritual.drawn.body')}
                        </Typography>
                        {dismissed && (
                            <Alert
                                severity="warning"
                                sx={{ mb: 2, textAlign: 'left' }}
                            >
                                {t('ritual.dismissed')}
                            </Alert>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            disabled={opening}
                            onClick={() => void reveal()}
                        >
                            {opening ? t('ritual.opening') : t('ritual.reveal')}
                        </Button>
                    </>
                )}
            </Stack>
        </PageShell>
    );
};
